import { useState, useMemo, useEffect } from "react";
import "./Dashboard.css"; // Importar animações
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Search, Filter, Package, Calendar, User, Copy, Edit, X, FileText, XCircle, MessageSquare, Lightbulb, FileJson, Zap, Users, ListOrdered, Printer, Share2 } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { OnboardingChecklist } from "@/components/OnboardingChecklist";
import { EvolvementCard } from "@/components/EvolvementCard";
import { DialogEnviarMensagem } from "@/components/DialogEnviarMensagem";
import { PageHeader } from "@/components/PageHeader";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { listOrders, updateOrderStatus } from "@/integrations/supabase/orders";
import { toast } from "sonner";
import { formatDateBR, parseISODateAsLocal } from "@/utils/dateOnly";
import { getOrderStatusColor, ORDER_STATUS_OPTIONS } from "@/utils/statusConstants";
import { useInternationalization } from "@/contexts/InternationalizationContext";
import { useAuth } from "@/components/AuthProvider";
import { PermissionGuard } from "@/components/PermissionGuard";
import { usePermission } from "@/hooks/usePermission";
import { getOnboardingProgress } from "@/integrations/supabase/onboarding";
import { ONBOARDING_STEPS } from "@/constants/onboarding";
import { getProductionTimeline, ProductionTimeline } from "@/integrations/supabase/production-timeline";
import { generateTimelineHTML, getTimelineStyles } from "@/utils/reportHelpers";

export default function Pedidos() {
  const { empresa } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterType, setFilterType] = useState<string>("all");
  const [filterPeriod, setFilterPeriod] = useState<string>("all");
  const [filterMonth, setFilterMonth] = useState<string>("");
  const [filterYear, setFilterYear] = useState<string>(new Date().getFullYear().toString());
  const [filterStartDate, setFilterStartDate] = useState<string>("");
  const [filterEndDate, setFilterEndDate] = useState<string>("");
  const [ordenacao, setOrdenacao] = useState<"recentes" | "antigos" | "cliente-az" | "cliente-za" | "valor-maior" | "valor-menor">("recentes");
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [itensPorPagina] = useState(10);
  const [enviarMensagemAberto, setEnviarMensagemAberto] = useState(false);
  const [pedidoMensagem, setPedidoMensagem] = useState<any>(null);

  // Modal PDF
  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
  const [pdfOptions, setPdfOptions] = useState({
    ordenacao: "recentes" as any,
    timeline: "completa" as "completa" | "status",
    clienteInfo: "completo" as "completo" | "nome",
    dataInicio: "",
    dataFim: "",
    formato: "pdf" as "pdf" | "excel",
  });

  // Estado para guardar timelines de produção
  const [timelinesMap, setTimelinesMap] = useState<Record<string, any>>({});

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { formatCurrency } = useInternationalization();

  // Buscar progresso do onboarding
  const { data: onboardingProgress } = useQuery({
    queryKey: ['onboarding-progress', empresa?.id],
    queryFn: () => getOnboardingProgress(empresa?.id!),
    enabled: !!empresa?.id,
  });

  // Buscar recebimentos para calcular valor pago correto
  const { data: recebimentos = [] } = useQuery({
    queryKey: ["recebimentos_todos"],
    queryFn: async () => {
      const { supabase } = await import("@/integrations/supabase/client");
      const { getCurrentEmpresaId } = await import("@/integrations/supabase/auth-utils");
      try {
        const empresaId = await getCurrentEmpresaId();
        if (!empresaId) return [];

        const { data, error } = await supabase
          .from('recebimentos_pedidos')
          .select('order_id, valor_recebido')
          .eq('empresa_id', empresaId);

        return error ? [] : (data || []);
      } catch {
        return [];
      }
    },
    staleTime: 10000,
  });

  const { data: ordersRaw = [] } = useQuery({
    queryKey: ["orders"],
    queryFn: listOrders,
    staleTime: 10000, // 10 segundos - dados considerados "frescos"
    refetchOnWindowFocus: true, // Atualizar quando a janela recebe foco
    refetchOnMount: true, // Sempre refetch ao montar o componente
    refetchInterval: 30000, // Atualizar a cada 30 segundos automaticamente
  });

  // Combinar dados de orders com recebimentos
  const orders = useMemo(() => {
    return ordersRaw.map((order: any) => {
      const rawName = order.customer_name ?? order.client ?? "";
      const customerName = typeof rawName === "string" ? rawName.trim() : "";
      const orderCode = order.code ?? order.id;
      const internalId = order.internalId ?? order.id;

      // Calcular valor pago a partir de recebimentos
      const recebimentosDoPedido = recebimentos.filter(r => r.order_id === order.id);
      const paidFromRecebimentos = recebimentosDoPedido.reduce((sum, r) => sum + Number(r.valor_recebido || 0), 0);

      return {
        id: orderCode, // Usar código do pedido como ID
        internalId, // Manter ID interno para referência
        client: customerName || "Cliente não informado",
        customer_name: order.customer_name,
        customer_phone: order.customer_phone,
        type: order.type,
        description: order.description ?? "",
        value: Number(order.value || 0),
        paid: paidFromRecebimentos > 0 ? paidFromRecebimentos : Number(order.paid || 0),
        delivery: order.delivery_date ?? order.delivery ?? "",
        status: order.status,
        discount: Number(order.discount || 0),
        discount_type: order.discount_type ?? 'fixed',
      };
    });
  }, [ordersRaw, recebimentos]);

  const getStatusColor = (status: string) => {
    return getOrderStatusColor(status);
  };

  // Carregar timelines com limit de requisições paralelas (batching)
  useEffect(() => {
    const loadTimelines = async () => {
      try {
        const newTimelinesMap: Record<string, any> = {};
        const batchSize = 10;

        for (let i = 0; i < orders.length; i += batchSize) {
          const batch = orders.slice(i, i + batchSize);

          const timelinePromises = batch.map(async (order) => {
            try {
              const timeline = await getProductionTimeline(order.internalId);
              return { orderId: order.internalId, timeline };
            } catch (error) {
              console.warn(`❌ Erro ao carregar timeline do pedido ${order.internalId}:`, error);
              return { orderId: order.internalId, timeline: { steps: [], current_step_key: null } };
            }
          });

          const results = await Promise.all(timelinePromises);
          results.forEach(({ orderId, timeline }) => {
            newTimelinesMap[orderId] = timeline;
          });

          setTimelinesMap(prev => ({ ...prev, ...newTimelinesMap }));
        }
      } catch (error) {
        console.error('❌ Erro ao carregar timelines:', error);
      }
    };

    if (orders.length > 0) {
      loadTimelines();
    }
  }, [orders]);

  // Memoizar mapa de statuses da timeline para evitar recalcular a cada render
  const timelineStatusMap = useMemo(() => {
    const statusMap: Record<string, string> = {};

    Object.entries(timelinesMap).forEach(([orderId, timeline]: [string, any]) => {
      if (!timeline || !timeline.steps || timeline.steps.length === 0 || !timeline.current_step_key) {
        return;
      }

      const currentStep = timeline.steps.find((s: any) => s.key === timeline.current_step_key);
      if (currentStep?.label) {
        statusMap[orderId] = currentStep.label;
      }
    });

    return statusMap;
  }, [timelinesMap]);

  // Função para pegar o status atual da timeline
  const getCurrentTimelineStatus = (orderId: string, defaultStatus: string): string => {
    return timelineStatusMap[orderId] || defaultStatus;
  };

  // Obter tipos únicos de pedidos
  const orderTypes = useMemo(() => {
    const types = new Set<string>();
    orders.forEach(order => {
      if (order.type) {
        types.add(order.type);
      }
    });
    return Array.from(types).sort();
  }, [orders]);

  // Filtrar pedidos
  const filteredOrders = useMemo(() => {
    let filtered = [...orders];

    // Filtro de busca
    if (searchTerm) {
      const term = searchTerm.toLowerCase().trim();
      const termsDigitsOnly = searchTerm.replace(/\D/g, '');

      filtered = filtered.filter(order => {
        const clientMatch = order.client?.toLowerCase().includes(term);
        const typeMatch = order.type?.toLowerCase().includes(term);
        const descriptionMatch = order.description?.toLowerCase().includes(term);

        // Buscar por código completo (ex: "PED-261019-215")
        const codeMatch = order.code?.toLowerCase().includes(term);

        // Buscar por partes do código (ex: "261019" ou "215" encontra "PED-261019-215")
        const codePartialMatch = termsDigitsOnly && order.code?.replace(/\D/g, '').includes(termsDigitsOnly);

        // Buscar por telefone/celular (ex: "11999999999" ou "11 99999-9999")
        const phoneDigitsOnly = order.customer_phone?.replace(/\D/g, '') || '';
        const phoneMatch = termsDigitsOnly && phoneDigitsOnly.includes(termsDigitsOnly);

        return clientMatch || typeMatch || codeMatch || codePartialMatch || descriptionMatch || phoneMatch;
      });
    }

    // Filtro de status
    if (filterStatus !== "all") {
      filtered = filtered.filter(order => order.status === filterStatus);
    }

    // Filtro de tipo
    if (filterType !== "all") {
      filtered = filtered.filter(order => order.type === filterType);
    }

    // Filtro de período
    let startDate: Date | null = null;
    let endDate: Date | null = null;

    if (filterPeriod !== "all") {
      const now = new Date();

      if (filterPeriod === "current_month") {
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
      } else if (filterPeriod === "last_month") {
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
      } else if (filterPeriod === "last_7_days") {
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        endDate = now;
      } else if (filterPeriod === "last_30_days") {
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        endDate = now;
      } else if (filterPeriod === "current_year") {
        startDate = new Date(now.getFullYear(), 0, 1);
        endDate = new Date(now.getFullYear(), 11, 31, 23, 59, 59);
      } else if (filterPeriod === "last_year") {
        startDate = new Date(now.getFullYear() - 1, 0, 1);
        endDate = new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59);
      }
    }

    // Filtro de mês/ano específico
    if (filterMonth && filterYear) {
      const year = parseInt(filterYear);
      const month = parseInt(filterMonth);
      startDate = new Date(year, month, 1);
      endDate = new Date(year, month + 1, 0, 23, 59, 59);
    }

    // Filtro de range de datas customizado
    if (filterStartDate) {
      startDate = new Date(filterStartDate);
      startDate.setHours(0, 0, 0, 0);
    }
    if (filterEndDate) {
      endDate = new Date(filterEndDate);
      endDate.setHours(23, 59, 59, 999);
    }

    // Aplicar filtro de datas se houver
    if (startDate || endDate) {
      filtered = filtered.filter(order => {
        if (!order.delivery) return false;
        const deliveryDate = parseISODateAsLocal(order.delivery);
        if (startDate && deliveryDate < startDate) return false;
        if (endDate && deliveryDate > endDate) return false;
        return true;
      });
    }

    // Ordenação
    filtered.sort((a, b) => {
      switch (ordenacao) {
        case "cliente-az":
          return a.client.localeCompare(b.client);
        case "cliente-za":
          return b.client.localeCompare(a.client);
        case "valor-maior":
          return b.value - a.value;
        case "valor-menor":
          return a.value - b.value;
        case "antigos":
          return new Date(a.delivery).getTime() - new Date(b.delivery).getTime();
        case "recentes":
        default:
          return new Date(b.delivery).getTime() - new Date(a.delivery).getTime();
      }
    });

    return filtered;
  }, [orders, searchTerm, filterStatus, filterType, filterPeriod, filterMonth, filterYear, filterStartDate, filterEndDate, ordenacao]);

  // Paginação
  const totalPaginas = Math.ceil(filteredOrders.length / itensPorPagina);
  const indiceInicio = (paginaAtual - 1) * itensPorPagina;
  const indiceFim = indiceInicio + itensPorPagina;
  const pedidosPaginados = filteredOrders.slice(indiceInicio, indiceFim);

  // Resetar para página 1 quando filtros mudam
  if (paginaAtual > 1 && filteredOrders.length === 0) {
    setPaginaAtual(1);
  }

  // Verificar se há filtros ativos
  const hasActiveFilters = filterStatus !== "all" || filterType !== "all" || filterPeriod !== "all" || filterMonth !== "" || filterStartDate !== "" || filterEndDate !== "";

  // Limpar filtros
  const clearFilters = () => {
    setFilterStatus("all");
    setFilterType("all");
    setFilterPeriod("all");
    setFilterMonth("");
    setFilterYear(new Date().getFullYear().toString());
    setFilterStartDate("");
    setFilterEndDate("");
    setIsFilterDialogOpen(false);
  };

  const duplicateOrder = (order: unknown) => {
    // Armazenar dados do pedido para duplicação
    const orderTyped = order as any;
    const orderData = {
      client: orderTyped.customer_name || orderTyped.client,
      type: orderTyped.type,
      description: orderTyped.description,
      value: orderTyped.value,
      // Não duplicar sinal pago e data de entrega
      paid: 0,
      delivery: "",
    };
    
    // Armazenar no localStorage para usar no NovoPedido
    localStorage.setItem('duplicateOrder', JSON.stringify(orderData));
    
    // Redirecionar para NovoPedido
    navigate('/pedidos/novo');
    
    toast.success("Dados do pedido copiados! Preencha as informações atualizadas.");
  };

  const handleCancelOrder = async (orderId: string) => {
    if (confirm("Tem certeza que deseja cancelar este pedido?")) {
      try {
        console.log("Cancelando pedido:", orderId);
        
        const result = await updateOrderStatus(orderId, "Cancelado");
        
        if (result.ok) {
          toast.success("Pedido cancelado com sucesso!");
          
          // Invalidar e recarregar os dados
          await queryClient.invalidateQueries({ queryKey: ["orders"] });
          await queryClient.invalidateQueries({ queryKey: ["order", orderId] });
          await queryClient.invalidateQueries({ queryKey: ["receitas"] }); // Sincronizar receitas
        } else {
          console.error("Erro ao cancelar pedido:", result.error);
          toast.error(result.error || "Erro ao cancelar pedido");
        }
      } catch (error) {
        console.error("Erro ao cancelar pedido:", error);
        toast.error("Erro ao cancelar pedido");
      }
    }
  };

  const generateProductionOrderPDF = async (orders: any[]) => {
    // Filtrar todos os pedidos em aberto (não cancelados nem entregues)
    const openOrders = orders.filter(order =>
      order.status !== "Cancelado" && order.status !== "Entregue"
    );

    if (openOrders.length === 0) {
      toast.warning("Nenhum pedido em aberto encontrado!");
      return;
    }

    // Mostrar loading
    toast.loading("Gerando relatório com timeline de produção...");

    // Criar conteúdo do PDF - Lista com Timeline de Produção
    // Buscar timeline de cada pedido de forma assíncrona
    const generatePrintContent = async () => {
      // Obter timelines de todos os pedidos
      const timelinesMap = new Map<string, ProductionTimeline>();

      for (const order of openOrders) {
        try {
          const timeline = await getProductionTimeline(order.id);
          timelinesMap.set(order.id, timeline);
        } catch (error) {
          console.warn(`Erro ao carregar timeline do pedido ${order.id}:`, error);
          timelinesMap.set(order.id, { steps: [], updated_at: new Date().toISOString() });
        }
      }

      const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>Lista de Pedidos em Aberto</title>
          <style>
            @media print {
              * { margin: 0; padding: 0; box-sizing: border-box; }
              body { font-family: Arial, sans-serif; font-size: 14px; line-height: 1.6; }
              .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 20px; }
              .header h1 { font-size: 28px; color: #333; margin-bottom: 10px; }
              .header p { color: #666; font-size: 16px; }
              .order-item {
                border: 1px solid #ddd;
                margin-bottom: 20px;
                padding: 15px;
                border-radius: 8px;
                background: #f9f9f9;
                page-break-inside: avoid;
              }
              .order-header {
                font-weight: bold;
                font-size: 16px;
                color: #2563eb;
                margin-bottom: 10px;
                display: flex;
                justify-content: space-between;
                align-items: center;
              }
              .order-details {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 10px;
                margin-bottom: 10px;
              }
              .detail-item {
                display: flex;
                flex-direction: column;
              }
              .detail-label {
                font-size: 12px;
                color: #666;
                font-weight: bold;
                text-transform: uppercase;
              }
              .detail-value {
                font-size: 14px;
                color: #333;
                margin-top: 2px;
              }
              .order-description {
                margin-top: 10px;
                padding: 10px;
                background: white;
                border-radius: 4px;
                border-left: 4px solid #2563eb;
              }
              .status-badge {
                padding: 4px 8px;
                border-radius: 4px;
                font-size: 12px;
                font-weight: bold;
              }
              .status-pending { background: #fef3c7; color: #92400e; }
              .status-production { background: #dbeafe; color: #1e40af; }
              .status-ready { background: #d1fae5; color: #065f46; }
              .status-waiting { background: #f3e8ff; color: #7c3aed; }
              .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; }

              ${getTimelineStyles()}
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>LISTA DE PEDIDOS EM ABERTO</h1>
            <p>Data: ${new Date().toLocaleDateString('pt-BR')}</p>
            <p>Total de Pedidos: ${openOrders.length}</p>
          </div>

          ${openOrders.map((order) => {
            // Extrair informações da descrição
            const description = order.description || '';
            const sizeMatch = description.match(/Tamanho:\\s*([^|]+)/);
            const colorMatch = description.match(/Cor:\\s*([^|]+)/);
            const size = sizeMatch ? sizeMatch[1].trim() : 'N/A';
            const color = colorMatch ? colorMatch[1].trim() : 'N/A';

            // Determinar classe do status
            const getStatusClass = (status) => {
              switch (status) {
                case "Aguardando aprovação": return "status-pending";
                case "Em produção": return "status-production";
                case "Pronto": return "status-ready";
                case "Aguardando retirada": return "status-waiting";
                default: return "status-pending";
              }
            };

            // Obter timeline do mapa
            const timeline = timelinesMap.get(order.id);
            const timelineHTML = timeline && timeline.steps && timeline.steps.length > 0
              ? generateTimelineHTML(timeline.steps)
              : '<p style="color: #999; font-size: 12px;">Nenhuma etapa de produção registrada</p>';

            return `
              <div class="order-item">
                <div class="order-header">
                  <span>Pedido #${order.id}</span>
                  <span class="status-badge ${getStatusClass(order.status)}">${order.status}</span>
                </div>
                <div class="order-details">
                  <div class="detail-item">
                    <span class="detail-label">Cliente</span>
                    <span class="detail-value">${order.client}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Tipo</span>
                    <span class="detail-value">${order.type}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Tamanho</span>
                    <span class="detail-value">${size}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Cor</span>
                    <span class="detail-value">${color}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Valor</span>
                    <span class="detail-value">${formatCurrency(order.value)}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Entrega</span>
                    <span class="detail-value">${order.delivery ? formatDateBR(order.delivery) : 'N/A'}</span>
                  </div>
                </div>
                <div class="order-description">
                  <strong>Descrição:</strong> ${description.replace(/\n/g, ' ')}
                </div>

                ${timelineHTML}
              </div>
            `;
          }).join('')}

          <div class="footer">
            <p>Gerado em ${new Date().toLocaleString('pt-BR')} - Ateliê Pro</p>
          </div>
        </body>
        </html>
      `;

      return printContent;
    };

    // Aguardar geração do conteúdo
    const printContent = await generatePrintContent();

    // Abrir janela de impressão
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      
      // Aguardar carregamento e imprimir
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 500);
      
      toast.success("Lista de pedidos gerada com sucesso!");
    } else {
      toast.error("Erro ao abrir janela de impressão!");
    }
  };

  const handleGeneratePDF = async () => {
    const openOrders = orders.filter(o => o.status !== "Cancelado" && o.status !== "Entregue");
    if (openOrders.length === 0) {
      toast.warning("Nenhum pedido aberto para gerar relatório");
      return;
    }

    toast.loading("Gerando PDF...");
    const logoUrl = empresa?.logo_url ? `${empresa.logo_url}?t=${Date.now()}` : "";
    const totalValue = openOrders.reduce((s, o) => s + Number(o.value || 0), 0);
    const totalPaid = openOrders.reduce((s, o) => s + Number(o.paid || 0), 0);
    const remaining = totalValue - totalPaid;

    // Carregar timelines em paralelo
    const timelinesMap = new Map<string, ProductionTimeline>();
    try {
      const timelinePromises = openOrders.map(async (order) => {
        const timeline = await getProductionTimeline(order.internalId || order.id);
        timelinesMap.set(order.id, timeline);
      });
      await Promise.all(timelinePromises);
    } catch (error) {
      console.error("Erro ao carregar timelines:", error);
    }

    // Função auxiliar para obter nome da etapa
    const getStepLabel = (order: any): string => {
      const timeline = timelinesMap.get(order.id);
      if (!timeline?.current_step_key) return order.status;
      const step = timeline.steps.find(s => s.key === timeline.current_step_key);
      return step?.label || order.status;
    };

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Pedidos em Aberto</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; color: #333; }
          .header { display: flex; align-items: center; gap: 20px; margin-bottom: 30px; border-bottom: 2px solid #7c3aed; padding-bottom: 20px; }
          .logo { max-width: 80px; }
          .logo img { max-width: 100%; }
          .header-title { flex: 1; }
          .header-title h1 { margin: 0; font-size: 28px; color: #7c3aed; }
          .header-title p { margin: 5px 0 0; color: #666; }
          .header-info { text-align: right; }
          .summary { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 20px; margin-bottom: 30px; }
          .summary-item { background: #f5f5f5; padding: 15px; border-radius: 8px; border-left: 4px solid #7c3aed; }
          .summary-item h3 { margin: 0 0 10px; font-size: 12px; color: #666; text-transform: uppercase; }
          .summary-item .value { font-size: 20px; font-weight: bold; color: #333; }
          .summary-item:nth-child(2) { border-left-color: #16a34a; }
          .summary-item:nth-child(2) .value { color: #16a34a; }
          .summary-item:nth-child(3) { border-left-color: #dc2626; }
          .summary-item:nth-child(3) .value { color: #dc2626; }
          table { width: 100%; border-collapse: collapse; }
          th { background: #7c3aed; color: white; padding: 12px; text-align: left; font-size: 13px; }
          td { padding: 12px; border-bottom: 1px solid #eee; }
          tr:hover { background: #f9f9f9; }
          .footer { margin-top: 40px; text-align: center; color: #999; font-size: 12px; }
        </style>
      </head>
      <body>
        <div class="header">
          ${logoUrl ? `<img src="${logoUrl}" class="logo" alt="Logo">` : ''}
          <div class="header-title">
            <h1>Pedidos em Aberto</h1>
            <p>Relatório de produção - ${new Date().toLocaleDateString('pt-BR')}</p>
          </div>
          <div class="header-info">
            <strong>${openOrders.length}</strong> pedidos
          </div>
        </div>

        <div class="summary">
          <div class="summary-item">
            <h3>Total</h3>
            <div class="value">${formatCurrency(totalValue)}</div>
          </div>
          <div class="summary-item">
            <h3>Recebido</h3>
            <div class="value">${formatCurrency(totalPaid)}</div>
          </div>
          <div class="summary-item">
            <h3>Pendente</h3>
            <div class="value">${formatCurrency(remaining)}</div>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th>Pedido</th>
              <th>Cliente</th>
              <th>Tipo</th>
              <th>Valor</th>
              <th>Entrega</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            ${openOrders.map((order, i) => `
              <tr>
                <td><strong>${order.id}</strong></td>
                <td>${order.client}</td>
                <td>${order.type || '—'}</td>
                <td>${formatCurrency(order.value)}</td>
                <td>${order.delivery ? new Date(order.delivery).toLocaleDateString('pt-BR') : '—'}</td>
                <td>${getStepLabel(order)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>

        <div class="footer">
          <p>Ateliê Pro • ${new Date().toLocaleString('pt-BR')}</p>
        </div>
      </body>
      </html>
    `;

    const win = window.open('', '_blank');
    if (win) {
      win.document.write(html);
      win.document.close();
      setTimeout(() => win.print(), 500);
      toast.success("PDF gerado com sucesso!");
    }
  };

  const handleGenerateExcel = async () => {
    const openOrders = orders.filter(o => o.status !== "Cancelado" && o.status !== "Entregue");

    // Carregar timelines em paralelo
    const timelinesMap = new Map<string, ProductionTimeline>();
    try {
      const timelinePromises = openOrders.map(async (order) => {
        const timeline = await getProductionTimeline(order.internalId || order.id);
        timelinesMap.set(order.id, timeline);
      });
      await Promise.all(timelinePromises);
    } catch (error) {
      console.error("Erro ao carregar timelines:", error);
    }

    // Função auxiliar para obter nome da etapa
    const getStepLabel = (order: any): string => {
      const timeline = timelinesMap.get(order.id);
      if (!timeline?.current_step_key) return order.status;
      const step = timeline.steps.find(s => s.key === timeline.current_step_key);
      return step?.label || order.status;
    };

    let csv = "Pedido,Cliente,Tipo,Valor,Recebido,Pendente,Entrega,Status\n";
    openOrders.forEach(o => {
      const pending = Number(o.value || 0) - Number(o.paid || 0);
      csv += `"${o.id}","${o.client}","${o.type || '—'}",${Number(o.value || 0).toFixed(2)},${Number(o.paid || 0).toFixed(2)},${pending.toFixed(2)},"${o.delivery || '—'}","${getStepLabel(o)}"\n`;
    });
    const blob = new Blob([csv], {type: 'text/csv'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pedidos_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success("Excel gerado com sucesso!");
  };

  return (
    <div className="min-h-screen bg-background">
      <PageHeader
        title="Gestão de Pedidos"
        description="Gerencie todos os pedidos do ateliê"
        icon={<Package className="w-6 h-6 text-purple-600" />}
        statusIndicatorsAbove
        leftActions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsPdfModalOpen(true)}
            className="flex-1 md:flex-none border-purple-400 text-purple-600 hover:bg-purple-50 hover:scale-105 transition-all duration-200 text-xs md:text-sm font-medium shadow-sm"
          >
            <FileText className="w-4 h-4 mr-1 md:mr-2" />
            <span className="hidden md:inline">Lista de Pedidos em Aberto</span>
            <span className="md:hidden">Lista PDF</span>
          </Button>
        }
        rightActions={
          <PermissionGuard module="pedidos" action="create">
            <Link to="/pedidos/novo" className="flex-1 md:flex-none">
              <Button size="sm" className="animated-gradient-button-purple w-full text-xs md:text-sm shadow-md">
                <Plus className="w-4 h-4 mr-1 md:mr-2" />
                <span className="hidden md:inline">Novo Pedido</span>
                <span className="md:hidden">Novo</span>
              </Button>
            </Link>
          </PermissionGuard>
        }
      />

      <div className="p-4 md:p-6 space-y-4 md:space-y-6 w-full overflow-x-hidden">
        <OnboardingChecklist />
        {/* Card de Evolução - Acompanhar o onboarding */}
        <EvolvementCard />
        {/* Filters */}
        <Card className="border-border w-full">
          <CardContent className="p-3 md:p-4 w-full">
            <div className="flex flex-col md:flex-row gap-3 md:gap-4 w-full">
              <div className="flex-1 relative w-full">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por cliente, tipo ou pedido..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 border-input w-full text-sm"
                />
              </div>
              <Button
                variant="outline"
                className={`border-border whitespace-nowrap flex-shrink-0 ${hasActiveFilters ? 'bg-primary/10 border-primary' : ''}`}
                onClick={() => setIsFilterDialogOpen(true)}
              >
                <Filter className="w-4 h-4 mr-2" />
                Filtrar
                {hasActiveFilters && (
                  <Badge variant="secondary" className="ml-2 h-5 w-5 rounded-full p-0 flex items-center justify-center">
                    <span className="text-xs">!</span>
                  </Badge>
                )}
              </Button>
              <select
                value={ordenacao}
                onChange={(e) => setOrdenacao(e.target.value as any)}
                className="px-3 py-2 border rounded-md border-input text-sm whitespace-nowrap flex-shrink-0"
                title="Ordenar"
              >
                <option value="recentes">Mais recentes</option>
                <option value="antigos">Mais antigos</option>
                <option value="cliente-az">Cliente (A-Z)</option>
                <option value="cliente-za">Cliente (Z-A)</option>
                <option value="valor-maior">Maior valor</option>
                <option value="valor-menor">Menor valor</option>
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Card Informativo de Onboarding - Etapa 3 (Evolução) */}
        {(onboardingProgress?.shouldShowEvolvementCard || onboardingProgress?.shouldShowChecklist) && onboardingProgress?.steps?.some(s => s.step === ONBOARDING_STEPS.CREATE_ORDER && !s.completed) && (
          <Card className="border-2 border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 mt-1">
                  <Lightbulb className="h-6 w-6 text-orange-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-orange-900 mb-1">💡 Etapa 3 de 5: Crie seu primeiro pedido</h3>
                  <p className="text-sm text-orange-800 mb-3">
                    Aprenda como criar pedidos diretos sem usar orçamentos. Clique no botão "+ Novo Pedido" para começar a criar seu primeiro pedido!
                  </p>
                  <div className="text-xs text-orange-700 bg-orange-100 rounded px-3 py-2 inline-block">
                    ✨ Dica: Você pode criar pedidos diretamente para seus clientes
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Contador de resultados */}
        {(searchTerm || hasActiveFilters) && (
          <div className="text-sm text-muted-foreground">
            Mostrando {filteredOrders.length} de {orders.length} pedido(s)
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="ml-2 h-auto p-1 text-xs"
              >
                <XCircle className="w-3 h-3 mr-1" />
                Limpar filtros
              </Button>
            )}
          </div>
        )}

        {/* Orders List */}
        <div className="grid gap-4 w-full">
          {filteredOrders.length === 0 ? (
            <Card className="border-border w-full">
              <CardContent className="p-8 text-center">
                <p className="text-muted-foreground">Nenhum pedido encontrado com os filtros aplicados.</p>
                {hasActiveFilters && (
                  <Button
                    variant="outline"
                    onClick={clearFilters}
                    className="mt-4"
                  >
                    Limpar filtros
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            pedidosPaginados.map((order) => (
            <Card
              key={order.id}
              className="border-2 border-gray-200 hover:border-purple-300 hover:shadow-xl transition-all duration-300 animate-fade-in cursor-pointer rounded-xl overflow-hidden group hover:scale-[1.01] w-full"
              onClick={() => navigate(`/pedidos/${order.id}`)}
            >
              <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50 border-b border-gray-200 p-3 md:p-6">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2 md:gap-4 min-w-0">
                  <div className="space-y-1 md:space-y-2 flex-1 min-w-0 overflow-hidden">
                    <CardTitle className="text-sm md:text-lg font-bold text-foreground flex items-center gap-2 group-hover:text-purple-600 transition-colors min-w-0">
                      <div className="p-2 bg-purple-100 rounded-lg group-hover:bg-purple-200 transition-colors flex-shrink-0">
                        <Package className="w-3 md:w-5 h-3 md:h-5 text-purple-600" />
                      </div>
                      <span className="truncate text-sm md:text-base">{order.id}</span>
                    </CardTitle>
                    <p className="text-xs md:text-sm text-muted-foreground font-medium break-words line-clamp-1">{order.type}</p>
                  </div>
                  <Badge variant="outline" className={`${getStatusColor(getCurrentTimelineStatus(order.internalId, order.status))} text-xs font-bold px-2 md:px-3 py-1 flex-shrink-0 whitespace-nowrap`}>
                    {getCurrentTimelineStatus(order.internalId, order.status)}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="p-3 md:p-6 overflow-x-hidden w-full">
                <div className="space-y-3 md:space-y-4 w-full">
                  <p className="text-foreground font-medium text-xs md:text-sm line-clamp-2 md:line-clamp-3 leading-snug break-words word-break">{order.description}</p>
                  
                  <div className={`space-y-2 md:space-y-0 md:grid md:gap-4 py-3 md:py-4 border-y border-gray-200 w-full ${order.discount > 0 ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
                    <div className="flex items-start gap-2 md:gap-3 p-2 md:p-3 bg-blue-50 rounded-lg border border-blue-100 w-full min-w-0">
                      <User className="w-4 md:w-5 h-4 md:h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-muted-foreground font-semibold uppercase tracking-tight">Cliente</p>
                        <p className="text-xs md:text-sm font-bold text-foreground mt-0.5 break-words word-break overflow-hidden">{order.client}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-2 md:gap-3 p-2 md:p-3 bg-purple-50 rounded-lg border border-purple-100 w-full min-w-0">
                      <Calendar className="w-4 md:w-5 h-4 md:h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-muted-foreground font-semibold uppercase tracking-tight">Entrega</p>
                        <p className="text-xs md:text-sm font-bold text-foreground mt-0.5">
                          {order.delivery ? formatDateBR(order.delivery) : 'Não definida'}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-2 md:gap-3 p-2 md:p-3 bg-green-50 rounded-lg border border-green-100 w-full min-w-0">
                      <div className="w-4 md:w-5 h-4 md:h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-green-600 font-bold text-xs md:text-sm">R$</span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-muted-foreground font-semibold uppercase tracking-tight">Valor / Pago</p>
                        <p className="text-xs md:text-sm font-bold text-foreground mt-0.5 break-words word-break">
                          {formatCurrency(order.value)} / {formatCurrency(order.paid)}
                        </p>
                      </div>
                    </div>

                    {order.discount > 0 && (
                      <div className="flex items-start gap-2 md:gap-3 p-2 md:p-3 bg-emerald-50 rounded-lg border border-emerald-100 w-full min-w-0">
                        <div className="w-4 md:w-5 h-4 md:h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-emerald-600 font-bold text-xs md:text-sm">R$</span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs text-muted-foreground font-semibold uppercase tracking-tight">C/ Desconto</p>
                          <p className="text-xs md:text-sm font-bold text-emerald-600 mt-0.5 break-words word-break">
                            {formatCurrency(
                              order.discount_type === 'percentage'
                                ? order.value * (1 - order.discount / 100)
                                : Math.max(0, order.value - order.discount)
                            )}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Botões de Ação */}
                  <div className="flex gap-1.5 md:gap-2 pt-3 md:pt-4 flex-wrap w-full overflow-x-hidden justify-start">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/pedidos/${order.id}`);
                      }}
                      className="border-purple-400 text-purple-600 hover:bg-purple-50 hover:scale-105 transition-all duration-200 font-medium text-xs md:text-sm px-2 md:px-3 py-1.5"
                    >
                      <FileText className="w-3 md:w-4 h-3 md:h-4 mr-1" />
                      <span>Abrir</span>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        duplicateOrder(order);
                      }}
                      className="border-purple-400 text-purple-600 hover:bg-purple-50 hover:scale-105 transition-all duration-200 font-medium text-xs md:text-sm px-2 md:px-3 py-1.5"
                    >
                      <Copy className="w-3 md:w-4 h-3 md:h-4 mr-1" />
                      <span className="hidden sm:inline">Duplicar</span>
                      <span className="sm:hidden">Dup.</span>
                    </Button>
                    <PermissionGuard module="pedidos" action="edit">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/pedidos/editar/${order.id}`);
                        }}
                        className="border-purple-400 text-purple-600 hover:bg-purple-50 hover:scale-105 transition-all duration-200 font-medium text-xs md:text-sm px-2 md:px-3 py-1.5"
                      >
                        <Edit className="w-3 md:w-4 h-3 md:h-4 mr-1" />
                        <span>Editar</span>
                      </Button>
                    </PermissionGuard>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPedidoMensagem(order);
                        setEnviarMensagemAberto(true);
                      }}
                      className="border-green-400 text-green-600 hover:bg-green-50 hover:scale-105 transition-all duration-200 font-medium text-xs md:text-sm px-2 md:px-3 py-1.5"
                    >
                      <MessageSquare className="w-3 md:w-4 h-3 md:h-4 mr-1" />
                      <span className="hidden sm:inline">Mensagem</span>
                      <span className="sm:hidden">Msg</span>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/pedidos/${order.id}/impressao`);
                      }}
                      className="border-blue-400 text-blue-600 hover:bg-blue-50 hover:scale-105 transition-all duration-200 font-medium text-xs md:text-sm px-2 md:px-3 py-1.5"
                    >
                      <Printer className="w-3 md:w-4 h-3 md:h-4 mr-1" />
                      <span className="hidden sm:inline">Imprimir</span>
                      <span className="sm:hidden">PDF</span>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        const shareLink = `${window.location.origin}/pedido/compartilhado/${order.id}`;
                        navigator.clipboard.writeText(shareLink);
                        toast.success('Link compartilhável copiado!', {
                          description: 'Envie este link para seu cliente acompanhar o pedido',
                          position: 'top-center'
                        });
                      }}
                      className="border-pink-400 text-pink-600 hover:bg-pink-50 hover:scale-105 transition-all duration-200 font-medium text-xs md:text-sm px-2 md:px-3 py-1.5"
                    >
                      <Share2 className="w-3 md:w-4 h-3 md:h-4 mr-1" />
                      <span className="hidden sm:inline">Compartilhar</span>
                      <span className="sm:hidden">Share</span>
                    </Button>
                    <PermissionGuard module="pedidos" action="cancel">
                      <Button
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCancelOrder(order.id);
                        }}
                        className="bg-red-500 hover:bg-red-600 hover:scale-105 transition-all duration-200 text-white font-medium text-xs md:text-sm px-2 md:px-3 py-1.5"
                      >
                        <X className="w-3 md:w-4 h-3 md:h-4 mr-1" />
                        <span className="hidden sm:inline">Cancelar</span>
                        <span className="sm:hidden">Canc.</span>
                      </Button>
                    </PermissionGuard>
                  </div>
                </div>
              </CardContent>
            </Card>
            ))
          )}

        {/* Controles de Paginação */}
        {filteredOrders.length > itensPorPagina && (
          <div className="mt-8 flex items-center justify-between flex-wrap gap-4 p-6 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border-2 border-gray-200">
            <div className="text-sm font-medium text-muted-foreground">
              Mostrando <span className="text-blue-600 font-bold">{indiceInicio + 1} a {Math.min(indiceFim, filteredOrders.length)}</span> de <span className="text-blue-600 font-bold">{filteredOrders.length}</span> pedidos
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPaginaAtual(p => Math.max(1, p - 1))}
                disabled={paginaAtual === 1}
                className="border-purple-400 text-purple-600 hover:bg-purple-100 hover:scale-105 transition-all duration-200 font-medium disabled:opacity-50"
              >
                ← Anterior
              </Button>
              <div className="flex items-center gap-2">
                {Array.from({ length: Math.min(5, totalPaginas) }).map((_, i) => {
                  let pagina = i + 1;
                  if (totalPaginas > 5) {
                    if (paginaAtual > 3) {
                      pagina = paginaAtual - 2 + i;
                    }
                  }
                  if (pagina > totalPaginas) return null;
                  return (
                    <Button
                      key={pagina}
                      variant={paginaAtual === pagina ? "default" : "outline"}
                      size="sm"
                      onClick={() => setPaginaAtual(pagina)}
                      className={paginaAtual === pagina ? "bg-purple-600 hover:bg-purple-700 scale-105 shadow-lg" : "border-purple-300 text-purple-600 hover:bg-purple-50 hover:scale-105 transition-all duration-200"}
                    >
                      {pagina}
                    </Button>
                  );
                })}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPaginaAtual(p => Math.min(totalPaginas, p + 1))}
                disabled={paginaAtual === totalPaginas}
                className="border-purple-400 text-purple-600 hover:bg-purple-100 hover:scale-105 transition-all duration-200 font-medium disabled:opacity-50"
              >
                Próxima →
              </Button>
            </div>
          </div>
        )}
        </div>
      </div>

      {/* Dialog de Enviar Mensagem */}
      {pedidoMensagem && (
        <DialogEnviarMensagem
          open={enviarMensagemAberto}
          onOpenChange={setEnviarMensagemAberto}
          pedidoId={pedidoMensagem.id}
          clienteNome={pedidoMensagem.customer_name || pedidoMensagem.client}
          clienteTelefone={pedidoMensagem.customer_phone || ''}
          pedidoCode={pedidoMensagem.id}
          pedidoCompleto={pedidoMensagem}
        />
      )}

      {/* Dialog de Filtros */}
      <Dialog open={isFilterDialogOpen} onOpenChange={setIsFilterDialogOpen}>
        <DialogContent className="w-[95vw] sm:w-full md:max-w-[500px] max-h-[90vh] overflow-y-auto rounded-lg p-4 sm:p-6">
          <DialogHeader className="pb-3 sm:pb-4 border-b border-slate-200 mb-2">
            <DialogTitle className="text-lg sm:text-xl font-bold">Filtrar Pedidos</DialogTitle>
          </DialogHeader>

          <div className="space-y-3 sm:space-y-4 py-2 sm:py-4">
            {/* Filtro de Status */}
            <div className="space-y-2 p-3 sm:p-4 rounded-lg bg-slate-50 border border-slate-200">
              <Label htmlFor="status-filter" className="text-sm font-semibold text-slate-700">Status</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger id="status-filter">
                  <SelectValue placeholder="Selecione um status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os status</SelectItem>
                  {ORDER_STATUS_OPTIONS.map((status) => (
                    <SelectItem key={status.value} value={status.value}>
                      {status.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Filtro de Tipo */}
            <div className="space-y-2 p-3 sm:p-4 rounded-lg bg-slate-50 border border-slate-200">
              <Label htmlFor="type-filter" className="text-sm font-semibold text-slate-700">Tipo de Pedido</Label>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger id="type-filter">
                  <SelectValue placeholder="Selecione um tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os tipos</SelectItem>
                  {orderTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Filtro de Período - Opções Pré-definidas */}
            <div className="space-y-2 p-3 sm:p-4 rounded-lg bg-slate-50 border border-slate-200">
              <Label htmlFor="period-filter" className="text-sm font-semibold text-slate-700">Período Pré-definido</Label>
              <Select value={filterPeriod} onValueChange={(value) => {
                setFilterPeriod(value);
                setFilterMonth("");
                setFilterStartDate("");
                setFilterEndDate("");
              }}>
                <SelectTrigger id="period-filter">
                  <SelectValue placeholder="Selecione um período" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todo o período</SelectItem>
                  <SelectItem value="last_7_days">Últimos 7 dias</SelectItem>
                  <SelectItem value="last_30_days">Últimos 30 dias</SelectItem>
                  <SelectItem value="current_month">Mês atual</SelectItem>
                  <SelectItem value="last_month">Mês anterior</SelectItem>
                  <SelectItem value="current_year">Este ano</SelectItem>
                  <SelectItem value="last_year">Ano anterior</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Filtro de Mês/Ano */}
            <div className="space-y-2 p-3 sm:p-4 rounded-lg bg-slate-50 border border-slate-200">
              <Label className="text-sm font-semibold text-slate-700">Filtrar por Mês/Ano</Label>
              <div className="grid grid-cols-2 gap-2">
                <Select value={filterMonth} onValueChange={(value) => {
                  setFilterMonth(value);
                  if (value) setFilterPeriod("all");
                  setFilterStartDate("");
                  setFilterEndDate("");
                }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Mês" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Janeiro</SelectItem>
                    <SelectItem value="1">Fevereiro</SelectItem>
                    <SelectItem value="2">Março</SelectItem>
                    <SelectItem value="3">Abril</SelectItem>
                    <SelectItem value="4">Maio</SelectItem>
                    <SelectItem value="5">Junho</SelectItem>
                    <SelectItem value="6">Julho</SelectItem>
                    <SelectItem value="7">Agosto</SelectItem>
                    <SelectItem value="8">Setembro</SelectItem>
                    <SelectItem value="9">Outubro</SelectItem>
                    <SelectItem value="10">Novembro</SelectItem>
                    <SelectItem value="11">Dezembro</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filterYear} onValueChange={(value) => {
                  setFilterYear(value);
                  if (filterMonth) setFilterPeriod("all");
                  setFilterStartDate("");
                  setFilterEndDate("");
                }}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 5 }, (_, i) => {
                      const year = new Date().getFullYear() - i;
                      return (
                        <SelectItem key={year} value={year.toString()}>
                          {year}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Filtro de Range de Datas */}
            <div className="space-y-2 p-3 sm:p-4 rounded-lg bg-slate-50 border border-slate-200">
              <Label className="text-sm font-semibold text-slate-700">Filtrar por Intervalo de Datas</Label>
              <div className="space-y-2">
                <div>
                  <Label className="text-xs sm:text-sm font-medium text-slate-600 block mb-1">Data Inicial</Label>
                  <Input
                    type="date"
                    value={filterStartDate}
                    onChange={(e) => {
                      setFilterStartDate(e.target.value);
                      if (e.target.value) {
                        setFilterPeriod("all");
                        setFilterMonth("");
                      }
                    }}
                    className="w-full text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs sm:text-sm font-medium text-slate-600 block mb-1">Data Final</Label>
                  <Input
                    type="date"
                    value={filterEndDate}
                    onChange={(e) => {
                      setFilterEndDate(e.target.value);
                      if (e.target.value) {
                        setFilterPeriod("all");
                        setFilterMonth("");
                      }
                    }}
                    className="w-full text-sm"
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="flex gap-2 flex-col-reverse sm:flex-row sm:gap-2 pt-4 sm:pt-6 border-t border-slate-200 mt-4 sm:mt-6">
            <Button
              variant="outline"
              onClick={clearFilters}
              className="w-full sm:w-auto text-sm sm:text-base"
            >
              Limpar Filtros
            </Button>
            <Button
              onClick={() => setIsFilterDialogOpen(false)}
              className="w-full sm:w-auto text-sm sm:text-base bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
            >
              Aplicar Filtros
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Opções PDF */}
      <Dialog open={isPdfModalOpen} onOpenChange={setIsPdfModalOpen}>
        <DialogContent className="w-[95vw] sm:w-full md:max-w-[500px] max-h-[90vh] overflow-y-auto bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 sm:p-6">
          <DialogHeader className="pb-3 sm:pb-4 border-b border-slate-200 mb-2">
            <DialogTitle className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              Configurar Relatório
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 sm:space-y-4 py-2 sm:py-4">
            {/* Formato */}
            <div className="space-y-2 sm:space-y-3 p-3 sm:p-4 rounded-lg bg-white border border-slate-200 hover:border-purple-300 transition-colors">
              <div className="flex items-center gap-2 min-w-0">
                <FileJson className="w-4 sm:w-5 h-4 sm:h-5 text-purple-600 flex-shrink-0" />
                <Label className="text-xs sm:text-sm font-semibold text-slate-700 truncate">Formato do Arquivo</Label>
              </div>
              <Select value={pdfOptions.formato} onValueChange={(v) => setPdfOptions({...pdfOptions, formato: v as any})}>
                <SelectTrigger className="bg-slate-50 border-slate-300 hover:border-purple-400 text-xs sm:text-sm h-9 sm:h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pdf">📄 PDF - Documento formatado</SelectItem>
                  <SelectItem value="excel">📊 Excel (CSV) - Planilha editável</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Ordenação */}
            <div className="space-y-2 sm:space-y-3 p-3 sm:p-4 rounded-lg bg-white border border-slate-200 hover:border-purple-300 transition-colors">
              <div className="flex items-center gap-2 min-w-0">
                <ListOrdered className="w-4 sm:w-5 h-4 sm:h-5 text-blue-600 flex-shrink-0" />
                <Label className="text-xs sm:text-sm font-semibold text-slate-700 truncate">Ordenação dos Pedidos</Label>
              </div>
              <Select value={pdfOptions.ordenacao} onValueChange={(v) => setPdfOptions({...pdfOptions, ordenacao: v as any})}>
                <SelectTrigger className="bg-slate-50 border-slate-300 hover:border-blue-400 text-xs sm:text-sm h-9 sm:h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recentes">🕐 Mais recentes</SelectItem>
                  <SelectItem value="antigos">🏛️ Mais antigos</SelectItem>
                  <SelectItem value="valor-maior">💰 Maior valor</SelectItem>
                  <SelectItem value="valor-menor">💵 Menor valor</SelectItem>
                  <SelectItem value="cliente-az">📇 Cliente (A-Z)</SelectItem>
                  <SelectItem value="cliente-za">📇 Cliente (Z-A)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Timeline */}
            <div className="space-y-2 sm:space-y-3 p-3 sm:p-4 rounded-lg bg-white border border-slate-200 hover:border-purple-300 transition-colors">
              <div className="flex items-center gap-2 min-w-0">
                <Zap className="w-4 sm:w-5 h-4 sm:h-5 text-amber-600 flex-shrink-0" />
                <Label className="text-xs sm:text-sm font-semibold text-slate-700 truncate">Exibição da Timeline</Label>
              </div>
              <Select value={pdfOptions.timeline} onValueChange={(v) => setPdfOptions({...pdfOptions, timeline: v as any})}>
                <SelectTrigger className="bg-slate-50 border-slate-300 hover:border-amber-400 text-xs sm:text-sm h-9 sm:h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="status">✓ Apenas status atual</SelectItem>
                  <SelectItem value="completa">📋 Timeline completa</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] sm:text-xs text-slate-500 mt-1.5 sm:mt-2 leading-relaxed">Status atual mostra a etapa atual de produção. Timeline completa exibe todo o histórico.</p>
            </div>

            {/* Dados do Cliente */}
            <div className="space-y-2 sm:space-y-3 p-3 sm:p-4 rounded-lg bg-white border border-slate-200 hover:border-purple-300 transition-colors">
              <div className="flex items-center gap-2 min-w-0">
                <Users className="w-4 sm:w-5 h-4 sm:h-5 text-green-600 flex-shrink-0" />
                <Label className="text-xs sm:text-sm font-semibold text-slate-700 truncate">Informações do Cliente</Label>
              </div>
              <Select value={pdfOptions.clienteInfo} onValueChange={(v) => setPdfOptions({...pdfOptions, clienteInfo: v as any})}>
                <SelectTrigger className="bg-slate-50 border-slate-300 hover:border-green-400 text-xs sm:text-sm h-9 sm:h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="nome">👤 Apenas nome</SelectItem>
                  <SelectItem value="completo">📋 Completo (tel, email)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter className="border-t border-slate-200 pt-3 sm:pt-4 mt-3 sm:mt-4 flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
            <Button
              variant="outline"
              onClick={() => setIsPdfModalOpen(false)}
              className="border-slate-300 hover:bg-slate-100 text-xs sm:text-sm h-9 sm:h-10"
            >
              Cancelar
            </Button>
            <Button
              onClick={() => {
                setIsPdfModalOpen(false);
                if (pdfOptions.formato === "excel") {
                  handleGenerateExcel();
                } else {
                  handleGeneratePDF();
                }
              }}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold shadow-md hover:shadow-lg transition-all text-xs sm:text-sm h-9 sm:h-10"
            >
              <FileText className="w-3.5 sm:w-4 h-3.5 sm:h-4 mr-1.5 sm:mr-2" />
              Gerar Relatório
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
